import { useCallback } from 'react';
import { PostgrestError } from '@supabase/supabase-js';
import { supabaseData } from '../../utils/supabase-data-client';
import { Category } from '../../types/bookmarks';
import { useCacheStore } from './useCacheStore';
import { useCacheFirstQuery } from './useCacheFirstQuery';

interface UseCategoriesReturn {
  categories: Category[];
  loading: boolean;
  error: PostgrestError | null;
  refetch: () => Promise<void>;
}

const useCategories = (parentId: string | null): UseCategoriesReturn => {
  const cache = useCacheStore();

  const cacheReader = cache.getCategories;

  const cacheFilter = useCallback(
    (items: Category[]) =>
      items
        .filter((c) =>
          parentId === null ? c.parent_id === null : c.parent_id === parentId
        )
        .sort((a, b) => a.category_name.localeCompare(b.category_name)),
    [parentId]
  );

  const networkFetch = useCallback(async () => {
    const { data, error } = await supabaseData
      .from('bookmark_categories')
      .select('*')
      .order('category_name');

    if (data) {
      cache.setCategories(data as Category[]);
      // Return filtered subset for this specific query
      const filtered = (data as Category[]).filter((c) =>
        parentId === null ? c.parent_id === null : c.parent_id === parentId
      );
      return { data: filtered, error };
    }

    return { data: data as Category[] | null, error };
  }, [cache, parentId]);

  const { data, loading, error, refetch } = useCacheFirstQuery<Category>({
    cacheReader,
    cacheFilter,
    networkFetch,
    queryKey: parentId ?? '__root__',
  });

  return { categories: data ?? [], loading, error, refetch };
};

export default useCategories;
